/**
 * Project lat/lon to 2D screen coords matching cobe's exact WebGL projection.
 *
 * Derived from cobe's fragment shader:
 * - World coords: (cos(lat)*cos(lon), sin(lat), -cos(lat)*sin(lon))
 * - Shader: m = l * L  ⟹  m = L^T * l  ⟹  l = L * m  (L orthogonal)
 * - Sphere radius = 0.8 in normalized [-1,1] space
 * - Canvas y-axis is flipped (top-down)
 */
export function projectToScreen(
  lat: number,
  lon: number,
  phi: number,
  theta: number,
  size: number,
): { x: number; y: number; visible: boolean } {
  const latR = (lat * Math.PI) / 180;
  const lonR = (lon * Math.PI) / 180;

  // World-space point (cobe convention)
  const mx = Math.cos(latR) * Math.cos(lonR);
  const my = Math.sin(latR);
  const mz = -Math.cos(latR) * Math.sin(lonR);

  const cp = Math.cos(phi);
  const sp = Math.sin(phi);
  const ct = Math.cos(theta);
  const st = Math.sin(theta);

  // l = L * m  (row-of-L dot m)
  // Row 0: (cp,    0,     sp)
  // Row 1: (sp*st, ct,   -cp*st)
  // Row 2: (-sp*ct, st,   cp*ct)
  const lx = cp * mx + sp * mz;
  const ly = sp * st * mx + ct * my - cp * st * mz;
  const lz = -sp * ct * mx + st * my + cp * ct * mz;

  // Sphere is 80% of the canvas (radius = 0.8 in normalized coords)
  const r = 0.8 * size / 2;

  return {
    x: lx * r + size / 2,
    y: size / 2 - ly * r,
    visible: lz > 0.02,
  };
}

/**
 * Compute cobe phi to center the globe on a given longitude.
 * With l = L * m, at theta=0: lz = -sin(lon + phi). Max when phi = -pi/2 - lon.
 */
export function phiForLongitude(lonDeg: number): number {
  return -Math.PI / 2 - (lonDeg * Math.PI) / 180;
}

/** Interpolate along a great circle arc, returning intermediate lat/lon pairs */
export function greatCirclePoints(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number,
  segments = 24,
): { lat: number; lon: number }[] {
  const lat1 = (fromLat * Math.PI) / 180;
  const lon1 = (fromLon * Math.PI) / 180;
  const lat2 = (toLat * Math.PI) / 180;
  const lon2 = (toLon * Math.PI) / 180;

  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2,
    ),
  );

  if (d < 0.0001) return [{ lat: fromLat, lon: fromLon }];

  const points: { lat: number; lon: number }[] = [];
  for (let i = 0; i <= segments; i++) {
    const f = i / segments;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);

    const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
    const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);

    points.push({
      lat: (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI,
      lon: (Math.atan2(y, x) * 180) / Math.PI,
    });
  }
  return points;
}
